<script setup>
import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import StaffChatContextMenu from './StaffChatContextMenu.vue';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['deleted', 'renamed']);

const { t } = useI18n();
const store = useStore();
const toggleContextMenu = inject('toggleContextMenu', () => {});

const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

const renameDialogRef = ref(null);
const deleteDialogRef = ref(null);
const groupName = ref('');
const isRenaming = ref(false);
const isDeleting = ref(false);

const isGroupChat = computed(() => props.chat?.chatType === 'group');

const deleteActionLabel = computed(() =>
  isGroupChat.value ? t('STAFF_CHAT.LEAVE_GROUP') : t('STAFF_CHAT.DELETE_CHAT')
);

const deleteDialogTitle = computed(() =>
  isGroupChat.value
    ? t('STAFF_CHAT.LEAVE_GROUP_TITLE')
    : t('STAFF_CHAT.DELETE_CHAT_TITLE')
);

const deleteDialogMessage = computed(() =>
  isGroupChat.value
    ? t('STAFF_CHAT.LEAVE_GROUP_MESSAGE')
    : t('STAFF_CHAT.DELETE_CHAT_MESSAGE')
);

const closeContextMenu = () => {
  toggleContextMenu(false);
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(
  () => props.chat?.id,
  () => {
    closeContextMenu();
  }
);

watch(
  () => props.chat?.name,
  name => {
    groupName.value = name || '';
  },
  { immediate: true }
);

const openContextMenu = event => {
  event.preventDefault();
  toggleContextMenu(true);
  contextMenu.value = {
    x: event.pageX || event.clientX,
    y: event.pageY || event.clientY,
  };
  showContextMenu.value = true;
};

const openRenameDialog = () => {
  groupName.value = props.chat?.name || '';
  renameDialogRef.value?.open();
};

const openDeleteDialog = () => {
  deleteDialogRef.value?.open();
};

const onRename = () => {
  closeContextMenu();
  openRenameDialog();
};

const onDelete = () => {
  closeContextMenu();
  openDeleteDialog();
};

const handleRename = async () => {
  if (!props.chat?.id) return;

  isRenaming.value = true;
  try {
    await store.dispatch('staffChats/updateStaffGroupChat', {
      chatId: props.chat.id,
      name: groupName.value.trim(),
    });
    useAlert(t('STAFF_CHAT.RENAME_SUCCESS'));
    renameDialogRef.value?.close();
    emit('renamed');
  } catch {
    useAlert(t('STAFF_CHAT.RENAME_ERROR'));
  } finally {
    isRenaming.value = false;
  }
};

const handleDelete = async () => {
  if (!props.chat?.id) return;

  isDeleting.value = true;
  try {
    await store.dispatch('staffChats/deleteStaffChat', props.chat.id);
    useAlert(t('STAFF_CHAT.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
    emit('deleted');
  } catch {
    useAlert(t('STAFF_CHAT.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};
</script>

<template>
  <slot :open-context-menu="openContextMenu" />

  <ContextMenu
    v-if="showContextMenu"
    :x="contextMenu.x"
    :y="contextMenu.y"
    @close="closeContextMenu"
  >
    <StaffChatContextMenu
      :is-group-chat="isGroupChat"
      @rename="onRename"
      @delete="onDelete"
    />
  </ContextMenu>

  <Dialog
    ref="renameDialogRef"
    type="edit"
    :title="$t('STAFF_CHAT.RENAME_GROUP_TITLE')"
    :description="$t('STAFF_CHAT.RENAME_GROUP_DESCRIPTION')"
    :confirm-button-label="$t('DIALOG.BUTTONS.CONFIRM')"
    :is-loading="isRenaming"
    @confirm="handleRename"
  >
    <input
      v-model="groupName"
      type="text"
      class="w-full px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
      :placeholder="$t('STAFF_CHAT.GROUP_NAME_PLACEHOLDER')"
    />
  </Dialog>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="deleteDialogTitle"
    :description="deleteDialogMessage"
    :confirm-button-label="deleteActionLabel"
    :is-loading="isDeleting"
    @confirm="handleDelete"
  />
</template>
